import React from "react";
import { Play } from "lucide-react";

export default function PortfolioSection() {
  const [activeVideo, setActiveVideo] = React.useState(null);
  const [isVisible, setIsVisible] = React.useState(false);
  const sectionRef = React.useRef(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  /* 9 VIDEO IDS */
  const videoIDs = [
    "akspPfyYdGk",
    "r2z5ijYN5Bs",
    "_bo5377c4Fc",
    "NZvMAl80BBQ",
    "b8dURxDBqxA",
    "vP1Okpi69eQ",
    "a_ReVnoIzYY",
    "cuSuwd6P_RQ",
    "ZwUDYapequY"
  ];

  /* Thumbnail Handler */
  const thumbnailFor = (id) => {
    return `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`;
  };

  return (
    <div
      ref={sectionRef}
      className="bg-transparent py-14 px-4 sm:px-6"
      id="works"
    >
      <div className="max-w-7xl mx-auto md:pt-[50px] pt-[0px]">
        {/* Badge */}
        <div className={`flex items-center mb-[4px] md:mb-[13px] justify-center gap-2 mb-4 border border-dashed border-neutral-300 shadow-sm h-[35px] w-[110px] mx-auto rounded-[40px] bg-white transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'}`}>
          <svg
            width="10"
            height="10"
            viewBox="0 0 256 256"
            fill="#3C5DF7"
            className="animate-spin"
            style={{ animationDuration: "5s" }}
          >
            <path d="M240,128a15.79,15.79,0,0,1-10.5,15l-63.44,23.07L143,229.5a16,16,0,0,1-30,0L89.94,166.06,26.5,143a16,16,0,0,1,0-30L89.94,89.94,113,26.5a16,16,0,0,1,30,0l23.07,63.44L229.5,113A15.79,15.79,0,0,1,240,128Z" />
          </svg>
          <span className="text-gray-600 font-medium text-[15px]">Works</span>
        </div>

        {/* Section Header */}
        <div className={`text-center mb-[35px] transition-all duration-700 delay-150 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-5xl md:text-6xl font-semibold tracking-tight mb-4">
            Our{" "}
            <span style={{ color: "#3C5DF7", fontFamily: "Recoleta" }}>
              Works
            </span>
          </h2>
          <p className="text-center text-[15.5px] text-[#555] max-w-[420px] mt-[10px] mb-[47px] leading-[1.6] font-[600] mx-auto">
            A showcase of the videos and podcasts crafted for creators and entrepreneurs.
          </p>
        </div>

        {/* 9-Video Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-[1200px] mx-auto">
          {videoIDs.map((id, index) => (
            <div
              key={index}
              className={`relative rounded-2xl overflow-hidden border border-gray-200 cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-500 aspect-video bg-gray-100 hover:scale-[1.02] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}
              style={{ 
                transitionDelay: `${300 + index * 100}ms`
              }}
            >
              {activeVideo === id ? (
                <iframe
                  className="w-full h-full"
                  src={`https://www.youtube.com/embed/${id}?autoplay=1`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <>
                  <img
                    src={thumbnailFor(id)}
                    onError={(e) => {
                      e.target.src = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
                    }}
                    alt="Video thumbnail"
                    className="w-full h-full object-cover hover:scale-110 transition-all duration-700"
                    onClick={() => setActiveVideo(id)}
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-60 hover:opacity-80 transition-all duration-300" />

                  <div className="absolute inset-0 flex items-center justify-center">
                    <div
                      onClick={() => setActiveVideo(id)}
                      className="w-16 h-16 bg-white/95 backdrop-blur-sm rounded-full flex items-center justify-center shadow-xl hover:scale-125 hover:bg-white transition-all duration-300 group"
                    >
                      <Play className="w-7 h-7 text-[#3C5DF7] ml-1 group-hover:scale-110 transition-transform" />
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}